
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePlaceForm } from './place-form/hooks/usePlaceForm';
import { PlaceFormContainer } from './place-form/PlaceFormContainer';
import { usePlaylistData, useSimilarPlaceCheck } from './place-form/hooks/usePlaceDataHelpers';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

interface AddPlaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  playlistId: string;
}

export function AddPlaceDialog({ isOpen, onClose, playlistId }: AddPlaceDialogProps) {
  const placeFormState = usePlaceForm({ playlistId, onClose });
  const { data: playlist } = usePlaylistData(playlistId);
  const { similarPlace, checkSimilarPlace, clearSimilarPlace } = useSimilarPlaceCheck();
  const [isExistingPlace, setIsExistingPlace] = useState(false);

  const handlePlaceSelect = async (placeData: any) => {
    // Fill in basic data from Google Places or from existing place
    placeFormState.setName(placeData.name);
    placeFormState.setPlaceType(placeData.place_type);
    placeFormState.setAddress(placeData.address);
    placeFormState.setUrl(placeData.url);
    placeFormState.setPhotos(placeData.photos);
    placeFormState.setPriceRange(placeData.price_range);
    placeFormState.setRequiresReservation(placeData.requires_reservation);
    
    // Only set city/country if not already defined in playlist
    if (!playlist?.city) placeFormState.setCity(placeData.city);
    if (!playlist?.country) placeFormState.setCountry(placeData.country);
    
    // Additional fields that might exist in an existing place
    if (placeData.note) placeFormState.setNote(placeData.note);
    if (placeData.cuisine_type) placeFormState.setCuisineType(placeData.cuisine_type);
    if (placeData.rating) placeFormState.setRating(placeData.rating.toString());
    if (placeData.amenities) placeFormState.setAmenities(placeData.amenities.join(', '));
    if (placeData.accessibility_features) placeFormState.setAccessibilityFeatures(placeData.accessibility_features.join(', '));
    if (placeData.best_time_to_visit) placeFormState.setBestTimeToVisit(placeData.best_time_to_visit);
    if (placeData.tags) placeFormState.setTags(placeData.tags.join(', '));
    
    // Check if this is being linked to an existing place
    setIsExistingPlace(!!placeData.isExistingPlace);
    
    // If not already determined by Google Places Search, check for similar places
    if (!placeData.isExistingPlace) {
      const foundPlace = await checkSimilarPlace(placeData.name, placeData.address);
      setIsExistingPlace(!!foundPlace);
    }
  };

  // Clear the existing place flag when the dialog is closed
  const handleClose = () => {
    setIsExistingPlace(false);
    clearSimilarPlace();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Place</DialogTitle>
          <DialogDescription>
            Add a new place to your collection
          </DialogDescription>
        </DialogHeader>
        
        {isExistingPlace && (
          <Alert className="bg-blue-50 border-blue-200 mb-4">
            <InfoIcon className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Place already exists</AlertTitle>
            <AlertDescription className="text-blue-700">
              This place already exists in another collection. The information has been pre-filled for you.
              When added, it will link to the original entry and any ratings will be shared.
            </AlertDescription>
          </Alert>
        )}
        
        <PlaceFormContainer 
          placeFormState={placeFormState}
          playlist={playlist}
          onClose={handleClose}
          handlePlaceSelect={handlePlaceSelect}
          isExistingPlace={isExistingPlace}
        />
      </DialogContent>
    </Dialog>
  );
}
